<template>
  <section class="index" data-aos="section-animation">
    <LazyPopup v-if="popupReady" />
    <HeroSection />
    <div style="min-height: 400px">
      <BarSection />
    </div>
    <div class="section" style="min-height: 400px">
      <DeferUntilVisible>
        <RoomSection />
        <template #fallback>
          <nav class="index-links" :aria-label="$t('rooms')">
            <h2 class="index-links__title">{{ $t('rooms_title') }}</h2>
            <ul class="index-links__list">
              <li v-for="room in roomLinks" :key="room.slug">
                <NuxtLink :to="`${langPrefix}/rooms/${room.slug}`">{{ room.title }}</NuxtLink>
              </li>
            </ul>
          </nav>
        </template>
      </DeferUntilVisible>
    </div>
    <div style="min-height: 400px">
      <RestaurantSection />
    </div>
    <div style="min-height: 400px">
      <DeferUntilVisible>
        <EventsSection />
        <template #fallback>
          <nav class="index-links" :aria-label="$t('event')">
            <h2 class="index-links__title">{{ $t('activities_section_title') }}</h2>
            <ul class="index-links__list">
              <li v-for="item in activityLinks" :key="item.slug">
                <NuxtLink :to="item.href">{{ item.title }}</NuxtLink>
              </li>
            </ul>
          </nav>
        </template>
      </DeferUntilVisible>
    </div>
    <div style="min-height: 400px">
      <SpaBanner />
    </div>
  </section>
  <div style="min-height: 400px">
    <LazyNewsLetter />
  </div>
</template>
<script>
import { defineAsyncComponent } from "vue";

// HeroSection stays a static, eagerly-bundled import — it's above the fold
// and must be part of the critical path.
import HeroSection from "/pages/index_components/HeroSection.vue";

// RestaurantSection/SpaBanner/BarSection render unconditionally on every
// request (SEO copy + images, no visibility gate — see below), so making
// them defineAsyncComponent bought nothing: Vue's SSR renderer still has to
// fully render them every time, it just also now has to await an extra
// dynamic import() per request first. Measured directly for Restaurant/Spa:
// this added ~28ms to Lighthouse's LCP "time to first byte" subpart (358ms
// -> 387ms), consistently, for zero benefit since there's no SSR work being
// skipped. Kept as plain static imports instead — same as HeroSection.
//
// BarSection used to sit in the deferred group below (it was previously
// wrapped in <ClientOnly>, so at the time nothing inside it had SSR output
// either way). It now contains the page's only <h1> plus always-visible
// tagline copy — real SEO/AEO content, not conditional UI — so it belongs
// here with Restaurant/Spa instead: deferring content that must always
// render server-side has the same zero-benefit profile measured above.
import RestaurantSection from "~/pages/index_components/RestaurantSection.vue";
import SpaBanner from "~/pages/index_components/SpaBanner.vue";
import BarSection from "~/pages/index_components/BarSection.vue";

// RoomSection/EventsSection are genuinely below-the-fold and never
// had SSR output (they were behind <ClientOnly> before). They're loaded via
// defineAsyncComponent (the same primitive Nuxt's own `Lazy` auto-import
// prefix compiles down to) and only mounted once their placeholder scrolls
// near the viewport (<DeferUntilVisible> below) — so unlike
// Restaurant/Spa/Bar, this genuinely removes their JS from the initial
// graph AND skips real SSR/hydration work until needed, at zero SEO/content
// cost versus before.
// (Nuxt's `Lazy` prefix only auto-registers components that live under
// ~/components; these live under ~/pages/index_components, so the
// equivalent is applied explicitly here rather than via that shorthand.)
const RoomSection = defineAsyncComponent(() => import("~/pages/index_components/RoomSection.vue"));
const EventsSection = defineAsyncComponent(() => import("~/pages/index_components/EventsSection.vue"));

import { HOTEL } from "~/utils/schema/hotel.entity.js";

const SITE_URL = HOTEL.SITE_URL;

const CONTACT_POINTS = [
  {
    '@type': 'ContactPoint',
    telephone: HOTEL.contact.phoneMain,
    contactType: 'reservations',
    areaServed: 'Worldwide',
    availableLanguage: ['Turkish', 'English'],
    contactOption: 'TollFree'
  },
  {
    '@type': 'ContactPoint',
    telephone: HOTEL.contact.phoneReception,
    contactType: 'customer support',
    areaServed: 'CY',
    availableLanguage: ['Turkish', 'English']
  },
  {
    '@type': 'ContactPoint',
    telephone: HOTEL.contact.phoneWhatsApp,
    contactType: 'customer support',
    areaServed: 'Worldwide',
    availableLanguage: ['Turkish', 'English']
  }
];

function buildGraph(locale, canonicalUrl, pageTitle, pageDescription) {
  const isEn = locale === 'en';
  const prefix = isEn ? 'en/' : '';

  const address = {
    '@type': 'PostalAddress',
    streetAddress: isEn ? HOTEL.address.streetAddressEN : HOTEL.address.streetAddressTR,
    addressLocality: isEn ? HOTEL.address.addressLocalityEN : HOTEL.address.addressLocalityTR,
    postalCode: HOTEL.address.postalCode,
    addressCountry: HOTEL.address.addressCountry
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: HOTEL.officialName,
        url: HOTEL.url,
        logo: { '@type': 'ImageObject', url: HOTEL.ogImage, width: 1200, height: 630 },
        address,
        contactPoint: CONTACT_POINTS,
        sameAs: HOTEL.sameAs
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: HOTEL.url,
        name: HOTEL.officialName,
        publisher: { '@id': `${SITE_URL}/#organization` },
        inLanguage: ['tr', 'en']
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}/#webpage`,
        url: canonicalUrl,
        name: pageTitle,
        description: pageDescription,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#hotel` },
        inLanguage: locale
      },
      {
        '@type': ['Hotel', 'Resort'],
        '@id': `${SITE_URL}/#hotel`,
        name: HOTEL.officialName,
        alternateName: HOTEL.alternateName,
        image: HOTEL.ogImage,
        url: HOTEL.url,
        telephone: HOTEL.contact.phoneMain,
        email: HOTEL.contact.email,
        address,
        geo: {
          '@type': 'GeoCoordinates',
          latitude: HOTEL.geo.latitude,
          longitude: HOTEL.geo.longitude
        },
        numberOfRooms: HOTEL.rooms.total,
        starRating: {
          '@type': 'Rating',
          ratingValue: HOTEL.rating.value,
          bestRating: HOTEL.rating.bestRating
        },
        priceRange: HOTEL.priceRange,
        checkinTime: HOTEL.checkinTime,
        checkoutTime: HOTEL.checkoutTime,
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
          opens: '00:00',
          closes: '23:59'
        },
        amenityFeature: HOTEL.amenities.map(a => ({
          '@type': 'LocationFeatureSpecification',
          name: a.name,
          value: a.value
        })),
        containsPlace: [
          {
            '@type': 'HealthAndBeautyBusiness',
            name: 'Lemon Spa & Wellness',
            url: `${SITE_URL}/${prefix}spa`
          },
          ...HOTEL.restaurants.map(r => ({
            '@type': 'FoodEstablishment',
            name: r.name,
            url: `${SITE_URL}/${prefix}flavors/${r.slug}`
          })),
          ...HOTEL.meetingHalls.map(h => ({
            '@type': 'EventVenue',
            name: h.name,
            url: `${SITE_URL}/${prefix}events/meeting-halls/${h.slug}`
          }))
        ],
        hasMap: `https://www.google.com/maps/place/Salamis+Hotel/@${HOTEL.geo.latitude},${HOTEL.geo.longitude},17z`,
        touristType: isEn
          ? ['Family', 'Couples', 'Business Travellers', 'Solo Travellers']
          : ['Aile', 'Çift', 'İş Seyahatçisi', 'Bireysel Gezgin'],
        isPartOf: { '@id': `${SITE_URL}/#organization` },
        containedInPlace: { '@id': `${SITE_URL}/#destination` },
        contactPoint: CONTACT_POINTS,
        sameAs: HOTEL.sameAs
      },
      {
        '@type': ['Place', 'TouristDestination'],
        '@id': `${SITE_URL}/#destination`,
        name: isEn ? 'Famagusta, North Cyprus' : 'Gazimağusa, Kuzey Kıbrıs',
        address,
        geo: {
          '@type': 'GeoCoordinates',
          latitude: HOTEL.geo.latitude,
          longitude: HOTEL.geo.longitude
        },
        includesAttraction: HOTEL.nearbyAttractions.map(a => ({
          '@type': a.type,
          name: a.name
        }))
      }
    ]
  };
}

export default {
  name: "Index",
  components: {
    RestaurantSection,
    SpaBanner,
    HeroSection,
    RoomSection,
    BarSection,
    EventsSection,
  },
  data() {
    return {
      isShow: true,
      data: null,
      popupData: null,
      popupImageUrl: null,
      // Popup has no SSR output (v-if="showPopup" inside it defaults
      // false), so deferring its own mount past the initial idle window
      // costs nothing visually — it just keeps its fetch/chunk out of the
      // critical first-load burst. Mirrors the requestIdleCallback +
      // setTimeout-fallback idiom already used in
      // plugins/smart-app-banner.client.ts.
      popupReady: false,
    };
  },
  methods: {
    schedulePopup() {
      const reveal = () => {
        this.popupReady = true;
      };
      if ("requestIdleCallback" in window) {
        requestIdleCallback(reveal, { timeout: 3000 });
      } else {
        setTimeout(reveal, 2000);
      }
    },
  },
  mounted() {
    this.schedulePopup();
  },
  async setup() {
    const { locale, setLocale, t } = useI18n();
    const store = generalStore();

    const language = computed({
      get: () => locale.value,
      set: (value) => { setLocale(value); },
    });

    const canonicalUrl = computed(() =>
      locale.value === 'en' ? `${SITE_URL}/en` : SITE_URL
    );

    const pageTitle = computed(() =>
      locale.value === 'en'
        ? 'Salamis Bay Conti Resort Hotel | Cyprus 5-Star Ultra All-Inclusive Hotel'
        : 'Salamis Bay Conti Resort Hotel | Kıbrıs 5 Yıldızlı Ultra Her Şey Dahil Otel'
    );

    const pageDescription = computed(() => t('page_meta_home'));

    useHead(() => ({
      title: pageTitle.value,
      titleTemplate: '%s',
      meta: [
        { name: 'description', content: pageDescription.value },
      ],
      link: [
        { rel: 'canonical', href: canonicalUrl.value },
      ],
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildGraph(
            locale.value,
            canonicalUrl.value,
            pageTitle.value,
            pageDescription.value
          ))
        }
      ]
    }));

    useSeoMeta({
      ogType:           'website',
      ogTitle:          pageTitle,
      ogDescription:    pageDescription,
      ogImage:          HOTEL.ogImage,
      ogImageWidth:     '1200',
      ogImageHeight:    '630',
      ogUrl:            canonicalUrl,
      ogSiteName:       HOTEL.officialName,
      twitterCard:      'summary_large_image',
      twitterTitle:     pageTitle,
      twitterDescription: pageDescription,
      twitterImage:     HOTEL.ogImage,
    });

    // Fired together (not sequential awaits) so the homepage's SSR TTFB pays
    // for the slower of the two requests, not both added up.
    const carouselPromise = useFetch(
      'https://api2.salamisbayconti.com/api/general/carousel',
      { key: 'index-carousel-covers' }
    );
    // Same key as Footer.vue's own `useAsyncData('footer-variables', ...)` —
    // on every other page that fetch stays server:false (fast TTFB), but the
    // homepage needs the CMS variable text (front_1, front_facilities, etc.)
    // present in the initial SSR HTML for crawlers that don't execute JS, so
    // it's fetched here with SSR blocking; Footer then reuses this cached
    // payload entry on the homepage instead of re-fetching client-side.
    const variablesPromise = useFetch(
      () => `https://api2.salamisbayconti.com/api/general/${locale.value}/variable`,
      { key: 'footer-variables' }
    );

    // Rooms/Events stay deferred (JS kept out of the initial bundle), but their
    // DeferUntilVisible fallback renders these as plain links server-side so
    // the homepage HTML still links to every room and event category.
    // transform keeps only what the links need out of the large API payloads.
    const pickLinkFields = (items) => (items || []).map(({ slug, title }) => ({ slug, title }));
    const roomsPromise = useFetch(
      () => `https://api2.salamisbayconti.com/api/general/${locale.value}/room`,
      { key: 'index-room-links', transform: pickLinkFields }
    );
    const activitiesPromise = useFetch(
      () => `https://api2.salamisbayconti.com/api/general/${locale.value}/activity`,
      { key: 'index-activity-links', transform: pickLinkFields }
    );

    const [{ data: carouselData }, { data: variablesData }, { data: roomsData }, { data: activitiesData }] =
      await Promise.all([carouselPromise, variablesPromise, roomsPromise, activitiesPromise]);
    if (carouselData.value) {
      store.setCovers(carouselData.value);
    }
    if (variablesData.value) {
      store.setVariables(variablesData.value);
    }

    const langPrefix = computed(() => (locale.value === 'en' ? '/en' : ''));
    const roomLinks = computed(() => roomsData.value || []);
    // Same destinations as EventsSection's cards (activityLink there).
    const ACTIVITY_TABS = {
      '?tab=concerts': '#concerts',
      '?tab=animations': '#animations',
      '?tab=meetings': '#meetings',
      '?tab=organizations': '#weddings',
    };
    const activityLinks = computed(() => (activitiesData.value || []).map((item) => {
      const base = `${langPrefix.value}/events`;
      const slug = ACTIVITY_TABS[item.slug] ?? item.slug;
      const href = slug && slug.startsWith('#') ? `${base}${slug}` : (slug ? `${base}/${slug}` : base);
      return { ...item, href };
    }));

    return { language, langPrefix, roomLinks, activityLinks };
  },
};
</script>
<style scoped>
.index {
  overflow-x: hidden !important;
}
/* Server-rendered stand-in for the deferred Rooms/Events sections; swapped
   for the real section once it scrolls near the viewport. */
.index-links {
  max-width: 1320px;
  margin: 0 auto;
  padding: clamp(56px, 6vw, 100px) clamp(20px, 4vw, 48px);
}
.index-links__title {
  margin: 0 0 20px;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(32px, 4vw, 52px);
  font-weight: 300;
  color: #0B1F2E;
}
.index-links__list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 28px;
  padding: 0;
  margin: 0;
}
.index-links__list a {
  font-family: 'Jost', sans-serif;
  font-size: .875rem;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: #0B1F2E;
  text-decoration: none;
  border-bottom: 1px solid currentColor;
}
</style>